/**
 * ModeMenu.jsx — Mode top-level menu.
 * Editing-mode checkboxes (Draft, Revise, Line Edit, etc.).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenuLabel, DropdownMenuCheckboxItem } from '@/components/ui/dropdown-menu';
import { usePW } from '@/lib/store';
import { Menu, useIsGuidanceMode } from './shared';

const MODE_TIPS = {
  draft:     'Write freely without rules — get words on the page',
  revise:    'Restructure scenes and improve the narrative arc',
  line_edit: 'Polish sentence-level clarity, rhythm, and word choice',
  proofread: 'Catch typos, grammar, and punctuation errors',
  outline:   'Plan your story beats and chapter structure',
};

export function ModeMenu() {
  const { t } = useTranslation();
  const { modes, activeMode, setActiveMode } = usePW();
  const isGuidance = useIsGuidanceMode();
  return (
    <Menu label={t('menu.mode')}>
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t('menu.editingModeLabel')}
      </DropdownMenuLabel>
      {modes.map(m => {
        const tip = MODE_TIPS[m.id] || MODE_TIPS[m.title?.toLowerCase()] || null;
        return (
          <DropdownMenuCheckboxItem
            key={m.id}
            checked={activeMode === m.id}
            onCheckedChange={(checked) => {
              if (!checked && activeMode === m.id) {
                // Re-clicking the already-active mode — reopen its companion panel
                window.dispatchEvent(new CustomEvent('loom:mode-reopen', { detail: { mode: m.id } }));
                return;
              }
              setActiveMode(m.id);
            }}
            className={`text-[12.5px] ${isGuidance && tip ? 'flex-col items-start gap-0 py-1.5' : ''}`}
          >
            <span>{m.title}</span>
            {isGuidance && tip && (
              <p className="mt-0.5 pl-[22px] text-[10.5px] leading-snug text-muted-foreground/75 font-normal">
                {tip}
              </p>
            )}
          </DropdownMenuCheckboxItem>
        );
      })}
    </Menu>
  );
}
